import React, { useState, useEffect } from 'react';
import { Bluetooth, Printer, Wifi, WifiOff, Settings, TestTube, Receipt, DollarSign } from 'lucide-react';
import { bluetoothPrinter, PrinterDevice } from '../../services/bluetoothPrinter';

const BluetoothPrinter: React.FC = () => {
  const [devices, setDevices] = useState<PrinterDevice[]>([]);
  const [isScanning, setIsScanning] = useState(false);
  const [isConnected, setIsConnected] = useState(false);
  const [connectedDevice, setConnectedDevice] = useState<PrinterDevice | null>(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isTestPrinting, setIsTestPrinting] = useState(false);
  const [isOpeningDrawer, setIsOpeningDrawer] = useState(false);

  useEffect(() => {
    checkConnectionStatus();
  }, []);

  const checkConnectionStatus = () => {
    const status = bluetoothPrinter.getConnectionStatus();
    setIsConnected(status.isConnected);
    setConnectedDevice(status.device);
  };

  const handleScanDevices = async () => {
    try {
      setIsScanning(true);
      setError('');
      setSuccess('');
      
      console.log('🔍 Starting device scan from UI...');
      console.log('📱 Platform info:', {
        isNative: (window as any).Capacitor?.isNativePlatform?.() || false,
        platform: (window as any).Capacitor?.getPlatform?.() || 'unknown',
        hasBluetoothPlugin: !!(window as any).bluetoothSerial
      });
      
      // Check if Bluetooth is supported first
      console.log('🔵 Checking Bluetooth support...');
      const isSupported = await bluetoothPrinter.isBluetoothSupported();
      console.log('🔵 Bluetooth support result:', isSupported);
      
      if (!isSupported) {
        const errorMsg = 'Bluetooth is not available on this device or is disabled. Please enable Bluetooth and try again.';
        console.error('❌', errorMsg);
        setError(errorMsg);
        return;
      }

      console.log('🔍 Starting device scan...');
      const foundDevices = await bluetoothPrinter.scanForDevices();
      console.log('📱 Found devices:', foundDevices);
      setDevices(foundDevices);
      
      if (foundDevices.length === 0) {
        const errorMsg = 'No Bluetooth devices found. Make sure your printer is in pairing mode and try again.';
        console.warn('⚠️', errorMsg);
        setError(errorMsg);
      } else {
        const successMsg = `Found ${foundDevices.length} Bluetooth device(s)`;
        console.log('✅', successMsg);
        setSuccess(successMsg);
      }
    } catch (error) {
      console.error('❌ Error scanning for devices:', error);
      const errorMsg = `Failed to scan for devices: ${error instanceof Error ? error.message : String(error)}`;
      setError(errorMsg);
    } finally {
      setIsScanning(false);
    }
  };

  const handleConnectDevice = async (device: PrinterDevice) => {
    try {
      setError('');
      setSuccess('');
      
      await bluetoothPrinter.connectToDevice(device);
      setIsConnected(true);
      setConnectedDevice(device);
      setSuccess(`Successfully connected to ${device.name}`);
    } catch (error) {
      console.error('Error connecting to device:', error);
      setError(`Failed to connect to ${device.name}. Please try again.`);
    }
  };

  const handleDisconnect = async () => {
    try {
      setError('');
      setSuccess('');
      
      await bluetoothPrinter.disconnect();
      setIsConnected(false);
      setConnectedDevice(null);
      setSuccess('Disconnected from printer');
    } catch (error) {
      console.error('Error disconnecting:', error);
      setError('Failed to disconnect from printer');
    }
  };

  const handleTestPrint = async () => {
    try {
      setIsTestPrinting(true);
      setError('');
      setSuccess('');
      
      await bluetoothPrinter.printTestPage();
      setSuccess('Test page sent to printer successfully!');
    } catch (error) {
      console.error('Error printing test page:', error);
      setError('Failed to print test page. Please check printer connection.');
    } finally {
      setIsTestPrinting(false);
    }
  };

  const handleOpenDrawer = async () => {
    try {
      setIsOpeningDrawer(true);
      setError('');
      setSuccess('');
      
      await bluetoothPrinter.openCashDrawer();
      setSuccess('Cash drawer command sent successfully!');
    } catch (error) {
      console.error('Error opening cash drawer:', error);
      setError('Failed to open cash drawer. Make sure a drawer is connected.');
    } finally {
      setIsOpeningDrawer(false);
    }
  };

  return (
    <div className="p-3 sm:p-6 max-w-4xl mx-auto animate-page-enter">
      <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-md rounded-lg shadow-lg border border-gray-200 dark:border-gray-700">
        {/* Header */}
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center space-x-3">
            <div className="bg-primary-100 dark:bg-primary-900/30 p-3 rounded-lg">
              <Bluetooth className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Bluetooth Thermal Printer</h1>
              <p className="text-gray-600 dark:text-gray-400 text-sm sm:text-base">Connect and manage your 2-inch thermal printer</p>
            </div>
          </div>
        </div>

        {/* Connection Status */}
        <div className="p-4 sm:p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
            <div className="flex items-center space-x-3">
              <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : 'bg-red-500'}`}></div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white">
                  {isConnected ? 'Connected' : 'Not Connected'}
                </h3>
                {connectedDevice && (
                  <p className="text-sm text-gray-600 dark:text-gray-400">{connectedDevice.name}</p>
                )}
              </div>
            </div>
            
            {isConnected && (
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2">
                <button
                  onClick={handleTestPrint}
                  disabled={isTestPrinting}
                  className="flex items-center justify-center space-x-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors duration-200"
                >
                  <TestTube className="w-4 h-4" />
                  <span className="text-sm">{isTestPrinting ? 'Printing...' : 'Test Print'}</span>
                </button>
                
                <button
                  onClick={handleOpenDrawer}
                  disabled={isOpeningDrawer}
                  className="flex items-center justify-center space-x-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 disabled:opacity-50 transition-colors duration-200"
                >
                  <DollarSign className="w-4 h-4" />
                  <span className="text-sm">{isOpeningDrawer ? 'Opening...' : 'Open Drawer'}</span>
                </button>
                
                <button
                  onClick={handleDisconnect}
                  className="flex items-center justify-center space-x-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors duration-200"
                >
                  <WifiOff className="w-4 h-4" />
                  <span className="text-sm">Disconnect</span>
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Error/Success Messages */}
        {error && (
          <div className="p-4 mx-4 sm:mx-6 mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
            <p className="text-red-700 dark:text-red-300 text-sm">{error}</p>
          </div>
        )}

        {success && (
          <div className="p-4 mx-4 sm:mx-6 mt-4 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg">
            <p className="text-green-700 dark:text-green-300 text-sm">{success}</p>
          </div>
        )}

        {/* Device Scanner */}
        <div className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-4 space-y-3 sm:space-y-0">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Available Devices</h3>
            <button
              onClick={handleScanDevices}
              disabled={isScanning}
              className="flex items-center justify-center space-x-2 bg-primary-600 dark:bg-primary-500 text-white px-4 py-2 rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 disabled:opacity-50 transition-colors duration-200 w-full sm:w-auto"
            >
              <Settings className={`w-4 h-4 ${isScanning ? 'animate-spin' : ''}`} />
              <span className="text-sm">{isScanning ? 'Scanning...' : 'Scan for Devices'}</span>
            </button>
          </div>

          {/* Device List */}
          <div className="space-y-3">
            {devices.length === 0 ? (
              <div className="text-center py-8">
                <Bluetooth className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-4" />
                <p className="text-gray-500 dark:text-gray-400">No devices found</p>
                <p className="text-gray-400 dark:text-gray-500 text-sm">Click "Scan for Devices" to find your thermal printer</p>
              </div>
            ) : (
              devices.map((device) => (
                <div
                  key={device.id}
                  className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors duration-200 space-y-3 sm:space-y-0"
                >
                  <div className="flex items-center space-x-3">
                    <div className="bg-gray-100 dark:bg-gray-700 p-2 rounded-lg">
                      <Printer className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                    </div>
                    <div>
                      <h4 className="font-medium text-gray-900 dark:text-white text-sm">{device.name}</h4>
                      <p className="text-sm text-gray-500 dark:text-gray-400">{device.address}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2">
                    {connectedDevice?.id === device.id ? (
                      <div className="flex items-center space-x-2 text-green-600 dark:text-green-400">
                        <Wifi className="w-4 h-4" />
                        <span className="text-sm font-medium">Connected</span>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleConnectDevice(device)}
                        className="bg-primary-600 dark:bg-primary-500 text-white px-3 py-1 rounded text-sm hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors duration-200 w-full sm:w-auto"
                      >
                        Connect
                      </button>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>

        {/* Instructions */}
        <div className="p-4 sm:p-6 bg-gray-50 dark:bg-gray-700/50 border-t border-gray-200 dark:border-gray-700">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">Setup Instructions</h3>
          <div className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
            <div className="flex items-start space-x-2">
              <span className="bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">1</span>
              <p>Make sure your 2-inch thermal printer is powered on and in pairing mode</p>
            </div>
            <div className="flex items-start space-x-2">
              <span className="bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">2</span>
              <p>Enable Bluetooth on your device if not already enabled</p>
            </div>
            <div className="flex items-start space-x-2">
              <span className="bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">3</span>
              <p>Click "Scan for Devices" to find your printer</p>
            </div>
            <div className="flex items-start space-x-2">
              <span className="bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">4</span>
              <p>Select your printer from the list and click "Connect"</p>
            </div>
            <div className="flex items-start space-x-2">
              <span className="bg-primary-100 dark:bg-primary-900/30 text-primary-600 dark:text-primary-400 rounded-full w-5 h-5 flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5">5</span>
              <p>Use "Test Print" to verify the connection works properly</p>
            </div>
          </div>
          
          <div className="mt-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
            <p className="text-yellow-700 dark:text-yellow-300 text-sm">
              <strong>Note:</strong> This feature works only in the mobile app (APK). 
              Web browser version will show connection options but won't actually connect to printers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BluetoothPrinter; 